import { RefObject, useCallback, useLayoutEffect, useState } from 'react'

type Subscriber = () => void

const subscribers = new Set<Subscriber>()
let activeSubscriptions = 0

const notifySubscribers = () => {
	subscribers.forEach((subscriber) => subscriber())
}

const startGlobalListeners = () => {
	document.addEventListener('scroll', notifySubscribers, {
		capture: true,
		passive: true,
	})
	window.addEventListener('resize', notifySubscribers)
}

const stopGlobalListeners = () => {
	document.removeEventListener('scroll', notifySubscribers, { capture: true })
	window.removeEventListener('resize', notifySubscribers)
}

const subscribe = (subscriber: Subscriber) => {
	subscribers.add(subscriber)
	if (activeSubscriptions === 0) {
		startGlobalListeners()
	}
	activeSubscriptions += 1
}

const unsubscribe = (subscriber: Subscriber) => {
	subscribers.delete(subscriber)
	activeSubscriptions -= 1
	if (activeSubscriptions === 0) {
		stopGlobalListeners()
	}
}

export const useElementBoundingRect = (
	anchorRef: RefObject<HTMLElement | null>,
	getTarget: (anchor: HTMLElement) => HTMLElement | null = (anchor) => anchor,
) => {
	const [rect, setRect] = useState<DOMRect | null>(null)

	const update = useCallback(() => {
		if (!anchorRef.current) {
			return
		}

		const target = getTarget(anchorRef.current)
		if (target) {
			setRect(target.getBoundingClientRect())
		}
	}, [anchorRef, getTarget])

	useLayoutEffect(() => {
		update()
		subscribe(update)

		const resizeObserver = new ResizeObserver(update)
		const target = anchorRef.current ? getTarget(anchorRef.current) : null
		if (target) {
			resizeObserver.observe(target)
		}

		return () => {
			unsubscribe(update)
			resizeObserver.disconnect()
		}
	}, [anchorRef, getTarget, update])

	return rect
}
